
import { useEffect } from "react";

interface MetaHeadProps {
  title?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
}

const MetaHead = ({ title, description, imageUrl, url }: MetaHeadProps) => {
  useEffect(() => {
    // Save original values to restore later
    const originalTitle = document.title;
    const metaTags: HTMLMetaElement[] = [];
    
    // Set document title if provided
    if (title) {
      document.title = title;
    }

    // Helper to create or update meta tags
    const setMetaTag = (property: string, content: string) => {
      // Try to find existing tag
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      
      // Create new tag if it doesn't exist
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
        metaTags.push(meta); // Track newly created tags
      }
      
      // Set content
      meta.setAttribute('content', content);
    };

    // Same for name-based meta tags
    const setNameMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
        metaTags.push(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set meta tags if values are provided
    if (description) {
      setMetaTag('og:description', description);
      setMetaTag('twitter:description', description);
      setNameMetaTag('description', description);
    }
    
    if (imageUrl) {
      let finalImageUrl = imageUrl;
      
      // Make absolute URL if it's relative
      if (imageUrl.startsWith('/')) {
        finalImageUrl = window.location.origin + imageUrl;
      } 
      // If it's a blob URL (for ImageRedirect), use the current page URL instead
      else if (imageUrl.startsWith('blob:')) {
        // For direct image links, use the page URL as the image URL for previews
        // Social media platforms can't access blob URLs, so they'll render the page instead
        finalImageUrl = window.location.href;
      }
      
      setMetaTag('og:image', finalImageUrl);
      setMetaTag('twitter:image', finalImageUrl);
      setMetaTag('og:image:type', 'image/png');
      setMetaTag('og:image:width', '1200');
      setMetaTag('og:image:height', '630');
    }
    
    if (title) {
      setMetaTag('og:title', title);
      setMetaTag('twitter:title', title);
    }
    
    if (url) {
      setMetaTag('og:url', url);
      setNameMetaTag('canonical', url);
    }
    
    // Always set these for Twitter and Open Graph
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('og:type', 'website');
    
    // Cleanup function to restore original state
    return () => {
      document.title = originalTitle;
      // Remove any meta tags we added
      metaTags.forEach(tag => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, [title, description, imageUrl, url]);

  return null; // This component doesn't render anything
};

export default MetaHead;
